import { useState } from "react";

import {
  usePendingActionsForGrow,
  useResolvePendingAction,
  useDismissPendingAction,
} from "@/lib/api/pending-actions";
import type { PendingAction } from "@/lib/api/types";
import type { RefillConversation } from "@/lib/refill/conversation";
import { Button } from "@/components/ui/button";
import { UrgencyBadge } from "@/components/urgency-badge";
import { EditActionForm } from "@/components/pending-actions/action-form";

// WizardPendingActions is the IN-WIZARD pending-actions workspace (step 3). It
// keeps the grower inside the refill flow instead of navigating out to
// /pending-actions (which used to unmount the wizard and drop the just-run AI
// analysis). The grower can work each action one-by-one — Mark done / Skip /
// Dismiss / Edit — and "Work with AI" appends a turn to the SHARED session
// conversation so the assistant can tell them what to do and create/update/
// close pending actions as they iterate. The resolve/dismiss hooks invalidate
// the per-grow cache, so the list refreshes in place as actions change.
//
// Crucially there is no "Start refill" CTA here: the grower is already mid-refill.
export function WizardPendingActions({
  growID,
  conv,
  disabled,
}: {
  growID: string;
  conv: RefillConversation;
  disabled?: boolean;
}) {
  const q = usePendingActionsForGrow(growID);
  const rows = (q.data?.items ?? []).filter((r) => !r.dismissed_at);

  return (
    <section className="space-y-3 rounded-md border bg-muted/20 p-4">
      <div className="space-y-1">
        <h3 className="text-sm font-medium">Pending actions for this grow</h3>
        <p className="text-xs text-muted-foreground">
          Work these as you go — Mark done / Skip / Dismiss, or ask the assistant
          to walk you through one. Changes sync to your pending actions and feed
          the same conversation as the analysis above.
        </p>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No open actions for this grow. Run the prioritized-actions step above —
          the assistant can create them.
        </p>
      ) : (
        <ul className="divide-y rounded-md border bg-background">
          {rows.map((row) => (
            <WizardActionRow
              key={row.id}
              row={row}
              conv={conv}
              disabled={disabled}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function WizardActionRow({
  row,
  conv,
  disabled,
}: {
  row: PendingAction;
  conv: RefillConversation;
  disabled?: boolean;
}) {
  const resolve = useResolvePendingAction(row.id, row.grow_id);
  const dismiss = useDismissPendingAction(row.id, row.grow_id);
  const [editing, setEditing] = useState(false);
  const busy = resolve.isPending || dismiss.isPending || disabled;

  function onSkip() {
    const reason = window.prompt("Reason for skipping? (optional)") || undefined;
    resolve.mutate({ resolution: "skipped", reason });
  }

  function workWithAI() {
    conv.run(
      `I'm working on this pending action: "${row.title}".` +
        (row.rationale ? ` Context: ${row.rationale}.` : "") +
        ` Given the current reservoir state and my chemistry profile, tell me exactly what to do for this now, step by step. Then create, update, or close my pending actions for this grow to reflect the plan.`,
    );
  }

  return (
    <li className="space-y-2 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <UrgencyBadge urgency={row.urgency} />
        <span className="font-medium">{row.title}</span>
        {row.llm_call_id && (
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2 py-0.5 text-xs font-medium text-violet-600 dark:text-violet-300">
            ✨ AI
          </span>
        )}
      </div>
      {row.rationale && (
        <p className="text-sm text-muted-foreground">{row.rationale}</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={busy || conv.isRunning}
          onClick={workWithAI}
        >
          Work with AI →
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => resolve.mutate({ resolution: "completed" })}
        >
          Mark done
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onSkip}>
          Skip
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => {
            const reason = window.prompt("Reason?") || undefined;
            dismiss.mutate(reason);
          }}
        >
          Dismiss
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => setEditing((v) => !v)}
        >
          {editing ? "Cancel" : "Edit"}
        </Button>
      </div>
      {editing && (
        <EditActionForm row={row} onDone={() => setEditing(false)} />
      )}
    </li>
  );
}
